import { makeAutoObservable, runInAction } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import {
  BrowsingProduct,
  CartItem,
  MarketingTag,
  Portfolio,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { BrowsingType } from 'types/domainServiceV2';
import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import { removePortfolioFromCart } from 'api/operations/cartServiceV2';
import { wipeRFQPortfolioData } from 'api/operations/productServiceV2';
import IProposalPageStore from '../iProposalPageStore';
import { INITIAL_CART_ITEM, INITIAL_PORTFOLIO, PortfolioToCartItem } from '../types';
import ProductLineDelegate from './productLineDelegate';

export interface ProductTagInfo {
  productId: string;
  tag: string;
  periodId: string;
}

interface Props<PageStore extends IProposalPageStore> {
  pageStore: PageStore;
  productLineDelegate: ProductLineDelegate<PageStore>;
  portfolioToCartItem: PortfolioToCartItem;
}

const INITIAL_TAG_INFO = {
  productId: '',
  tag: '',
  periodId: '',
};

export default class CartItemDelegate<PageStore extends IProposalPageStore> {
  private pageStore: PageStore;
  private productLineDelegate: ProductLineDelegate<PageStore>;

  // Maybe just cast those types and don't describe all this stuff here??? You actually need only 2-3 props from there, that's it
  private portfolio: Portfolio = { ...INITIAL_PORTFOLIO };

  // Same here
  private cartItem: CartItem = { ...INITIAL_CART_ITEM };

  public createManageTagModalDelegate = new ModalDelegate();
  private _activeProductInfo: ProductTagInfo = INITIAL_TAG_INFO;
  public createRemovePortfolioModalDelegate = new ModalDelegate();

  public setActiveProductInfo(info: ProductTagInfo): void {
    this._activeProductInfo = { ...info };
  }

  public openManageTagModal(info: ProductTagInfo): void {
    this.setActiveProductInfo(info);
    this.createManageTagModalDelegate.open();
  }

  public getProductBrowsingInfo(productId: string): BrowsingProduct {
    const res = this.wholePortfolio.products.find((product) => product.imProductId === productId);
    if (!res) {
      throw new Error(`No product with id: ${productId} found in the portfolio with id: ${this.portfolioId}`);
    }
    return res;
  }

  public get modalIsVisible(): boolean {
    return this.createRemovePortfolioModalDelegate.isVisible;
  }

  public openRemovePortfolioModal(): void {
    this.createRemovePortfolioModalDelegate.open();
  }

  public closeRemovePortfolioModal(): void {
    this.createRemovePortfolioModalDelegate.close();
  }

  public async removePortfolio(): Promise<void> {
    this.createRemovePortfolioModalDelegate.close();
    const cartId = this.pageStore.initialStore.cart.id;
    this.pageStore.initialStore.setLoading(true);
    const res = await removePortfolioFromCart(cartId, this.portfolioId, false);
    await runInAction(async () => {
      const currentCart = res.data?.removePortfolioConfigFromCart;

      if (this.browsingType === 'RFQ') {
        await wipeRFQPortfolioData(cartId, this.portfolioId);
      }
      if (currentCart) {
        await this.pageStore.updateCustomPortfolioAndCart(currentCart, this.portfolio.id, false);
      }

      this.pageStore.initialStore.setLoading(false);
    });
  }

  public get portfolioId(): string {
    return this.portfolio.id;
  }

  public get portfolioName(): string {
    return this.portfolio.name;
  }

  public get isPortfolioLoading(): boolean {
    return this.pageStore.initialStore.portfolioLoading[this.portfolioId];
  }

  public get isPortfolioLoadingWithRates(): boolean {
    return this.isPortfolioLoading && !!this.pageStore.initialStore.removedRateInfo.portfolioId;
  }

  public get browsingType(): BrowsingType {
    return this.pageStore.initialStore.getProductLineBrowsingType(this.productLineDelegate.productLineId);
  }

  public get overrideRatesEligible(): boolean {
    return this.pageStore.initialStore.getOverrideRatesEligible(this.productLineDelegate.productLineId);
  }

  public get marketingTags(): MarketingTag[] {
    return this.pageStore.initialStore.marketingTags;
  }

  /**
   * Such usage is discouraged, create explicit interfaces instead
   */
  public get wholePortfolio(): Portfolio {
    return this.portfolio;
  }

  /**
   * Such usage is discouraged, create explicit interfaces instead
   */
  public get wholeCartItem(): CartItem {
    return this.cartItem;
  }

  public get isPlanConfigurable(): boolean {
    return (
      this.pageStore.getDomainProductLine(this.productLineDelegate.productLineId)?.browsingType === 'SHELF' &&
      this.pageStore.initialStore.isPlanConfigurable(this.portfolio)
    );
  }

  public get isProposalConfirmed(): boolean {
    return this.pageStore.initialStore.isProposalConfirmed;
  }

  public get userRoles(): SupportedUserRoles[] {
    return this.pageStore.initialStore.rootStore.userStore.userRoles;
  }

  public get activeProductInfo(): ProductTagInfo {
    return this._activeProductInfo;
  }

  constructor({ pageStore, productLineDelegate, portfolioToCartItem }: Props<PageStore>) {
    this.pageStore = pageStore;
    this.productLineDelegate = productLineDelegate;
    this.portfolio = portfolioToCartItem.portfolio;
    this.cartItem = portfolioToCartItem.cartItem;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
